import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, jadeGreen, goldenTopaz, garnetRed } from '../design-system/theme';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import {
  PageHeader,
  PageTitle,
  FilterGroup,
  SearchInput,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ActionButtons,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  Label,
  Textarea,
  Select,
  AlertMessage,
  LoadingSpinner,
} from '../styles/adminShared';

// Types
interface WithdrawalRequest {
  withdrawalId: string;
  tenantId: string;
  consentRecordId: string;
  dataOwnerId: string;
  reason?: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'REJECTED';
  requestedBy?: string;
  processedBy?: string;
  requestedAt: string;
  processedAt?: string;
  verified?: boolean;
  verificationMethod?: string;
  verificationDetails?: string;
  dataErasureRequired?: boolean;
  dataErasureCompleted?: boolean;
  notes?: string;
  rejectionReason?: string;
}

// Page-specific styled components (only those not in shared)
const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCardStyled = styled.div<{ $color?: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${props => props.$color || theme.primary};
  
  .value {
    font-size: 28px;
    font-weight: 700;
    color: ${theme.textPrimary};
  }
  
  .label {
    font-size: 13px;
    color: ${theme.textSecondary};
    margin-top: 4px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const VerifiedBadge = styled.span<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$verified ? jadeGreen[1] : goldenTopaz[1]};
  color: ${props => props.$verified ? jadeGreen[7] : goldenTopaz[8]};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const PaginationInfo = styled.span`
  color: ${theme.textSecondary};
  font-size: 14px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border: 2px solid ${props => props.$active ? theme.primary : neutral[4]};
  border-radius: 6px;
  background: ${props => props.$active ? theme.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    border-color: ${theme.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  color: ${blueGrey[8]};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  padding: 12px;
  background: ${neutral[1]};
  border-radius: 8px;
  
  .label {
    font-size: 11px;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 14px;
    color: ${theme.textPrimary};
    word-break: break-all;
    
    &.mono {
      font-family: 'SF Mono', monospace;
      font-size: 12px;
    }
  }
`;

export const WithdrawalRequests: React.FC = () => {
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  
  // Modals
  const [selectedRequest, setSelectedRequest] = useState<WithdrawalRequest | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    rejected: 0,
  });

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminApi.getWithdrawalRequests({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: statusFilter || undefined,
        query: query || undefined,
      });

      setRequests(response.data || []);
      setPagination(prev => ({
        ...prev,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0,
      }));

      // Calculate stats from all data
      const allRequests = response.data || [];
      setStats({
        pending: allRequests.filter(r => r.status === 'PENDING').length,
        processing: allRequests.filter(r => r.status === 'PROCESSING').length,
        completed: allRequests.filter(r => r.status === 'COMPLETED').length,
        rejected: allRequests.filter(r => r.status === 'REJECTED').length,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load withdrawal requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, statusFilter, query]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApprove = async (request: WithdrawalRequest) => {
    try {
      setProcessing(true);
      setError(null);
      
      await adminApi.approveWithdrawalRequest(request.withdrawalId);
      
      setSuccess('Withdrawal request approved successfully');
      setSelectedRequest(null);
      loadRequests();
    } catch (err: any) {
      setError(err.message || 'Failed to approve withdrawal request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) return;
    
    try {
      setProcessing(true);
      setError(null);
      
      await adminApi.rejectWithdrawalRequest(selectedRequest.withdrawalId, rejectReason);
      
      setSuccess('Withdrawal request rejected');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      loadRequests();
    } catch (err: any) {
      setError(err.message || 'Failed to reject withdrawal request');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminShell title="Withdrawal Requests">
      <PageHeader>
        <TitleSection>
          <PageTitle>🚫 Withdrawal Requests</PageTitle>
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
            Manage consent withdrawal requests from data owners (GDPR Article 7(3))
          </div>
        </TitleSection>
      </PageHeader>

      <StatsRow>
        <StatCardStyled $color="#f39c12">
          <div className="value">{stats.pending}</div>
          <div className="label">Pending Review</div>
        </StatCardStyled>
        <StatCardStyled $color="#3498db">
          <div className="value">{stats.processing}</div>
          <div className="label">Processing</div>
        </StatCardStyled>
        <StatCardStyled $color="#27ae60">
          <div className="value">{stats.completed}</div>
          <div className="label">Completed</div>
        </StatCardStyled>
        <StatCardStyled $color="#e74c3c">
          <div className="value">{stats.rejected}</div>
          <div className="label">Rejected</div>
        </StatCardStyled>
      </StatsRow>

      <FilterBar>
        <FilterGroup>
          <Label>Search</Label>
          <SearchInput
            type="text"
            placeholder="Search by ID, data owner..."
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setQuery(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && loadRequests()}
          />
        </FilterGroup>
        
        <FilterGroup>
          <Label>Status</Label>
          <Select value={statusFilter} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="REJECTED">Rejected</option>
          </Select>
        </FilterGroup>
      </FilterBar>

      {error && <AlertMessage $variant="error">⚠️ {error}</AlertMessage>}
      {success && <AlertMessage $variant="success">✅ {success}</AlertMessage>}

      {loading ? (
        <LoadingSpinner>Loading withdrawal requests...</LoadingSpinner>
      ) : requests.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 40px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
          <h3 style={{ color: theme.textPrimary, margin: '0 0 8px 0' }}>No Withdrawal Requests</h3>
          <p style={{ color: theme.textSecondary, margin: 0 }}>No withdrawal requests match your current filters</p>
        </div>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Request ID</Th>
                <Th>Data Owner</Th>
                <Th>Requested</Th>
                <Th>Reason</Th>
                <Th>Status</Th>
                <Th>Verified</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <Tr key={request.withdrawalId || request.dataOwnerId}>
                  <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {request.withdrawalId?.substring(0, 12) || 'N/A'}...
                  </Td>
                  <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {request.dataOwnerId?.substring(0, 12) || 'N/A'}...
                  </Td>
                  <Td style={{ fontSize: '13px' }}>
                    {formatDate(request.requestedAt)}
                  </Td>
                  <Td style={{ maxWidth: '200px' }}>
                    {request.reason 
                      ? request.reason.length > 50 
                        ? `${request.reason.substring(0, 50)}...` 
                        : request.reason
                      : <span style={{ color: theme.textSecondary }}>No reason provided</span>
                    }
                  </Td>
                  <Td>
                    <StatusBadge $status={request.status}>{request.status}</StatusBadge>
                  </Td>
                  <Td>
                    <VerifiedBadge $verified={!!request.verified}>
                      {request.verified ? '✓ Verified' : '○ Pending'}
                    </VerifiedBadge>
                  </Td>
                  <Td>
                    <ActionButtons>
                      <IconButton 
                        $variant="view" 
                        onClick={() => setSelectedRequest(request)}
                      >
                        👁️ View
                      </IconButton>
                      {request.status === 'PENDING' && (
                        <>
                          <IconButton 
                            $variant="success"
                            onClick={() => handleApprove(request)}
                            disabled={processing}
                          >
                            ✓ Approve
                          </IconButton>
                          <IconButton 
                            $variant="danger"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowRejectModal(true);
                            }}
                            disabled={processing}
                          >
                            ✕ Reject
                          </IconButton>
                        </>
                      )}
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          <PaginationContainer>
            <PaginationInfo>
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems}
            </PaginationInfo>
            <PaginationButtons>
              <PaginationButton
                disabled={pagination.page <= 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                ← Previous
              </PaginationButton>
              <PaginationButton $active>{pagination.page}</PaginationButton>
              <PaginationButton
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next →
              </PaginationButton>
            </PaginationButtons>
          </PaginationContainer>
        </>
      )}

      {/* View Detail Modal */}
      <ModalOverlay $show={!!selectedRequest && !showRejectModal} onClick={() => setSelectedRequest(null)}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>🚫 Withdrawal Request Details</ModalTitle>
            <CloseButton onClick={() => setSelectedRequest(null)}>×</CloseButton>
          </ModalHeader>
          {selectedRequest && (
            <ModalBody>
              <DetailSection>
                <SectionTitle>📌 Request Information</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Withdrawal ID</div>
                    <div className="value mono">{selectedRequest.withdrawalId}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Status</div>
                    <div className="value">
                      <StatusBadge $status={selectedRequest.status}>{selectedRequest.status}</StatusBadge>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Requested At</div>
                    <div className="value">{formatDate(selectedRequest.requestedAt)}</div>
                  </DetailItem>
                  {selectedRequest.processedAt && (
                    <DetailItem>
                      <div className="label">Processed At</div>
                      <div className="value">{formatDate(selectedRequest.processedAt)}</div>
                    </DetailItem>
                  )}
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>👤 Data Owner</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Data Owner ID</div>
                    <div className="value mono">{selectedRequest.dataOwnerId}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Consent Artifact ID</div>
                    <div className="value mono">{selectedRequest.consentRecordId}</div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>📝 Reason</SectionTitle>
                <DetailItem style={{ gridColumn: '1 / -1' }}>
                  <div className="value">{selectedRequest.reason || 'No reason provided'}</div>
                </DetailItem>
              </DetailSection>

              <DetailSection>
                <SectionTitle>✓ Verification</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Verified</div>
                    <div className="value">
                      <VerifiedBadge $verified={!!selectedRequest.verified}>
                        {selectedRequest.verified ? '✓ Verified' : '○ Not Verified'}
                      </VerifiedBadge>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Method</div>
                    <div className="value">{selectedRequest.verificationMethod || 'N/A'}</div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>🗑️ Data Erasure</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Erasure Required</div>
                    <div className="value">{selectedRequest.dataErasureRequired ? '✅ Yes' : '❌ No'}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Erasure Completed</div>
                    <div className="value">{selectedRequest.dataErasureCompleted ? '✅ Yes' : '⏳ Pending'}</div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {selectedRequest.rejectionReason && (
                <DetailSection>
                  <SectionTitle>⚠️ Rejection Reason</SectionTitle>
                  <DetailItem style={{ background: garnetRed[1] }}>
                    <div className="value" style={{ color: garnetRed[8] }}>{selectedRequest.rejectionReason}</div>
                  </DetailItem>
                </DetailSection>
              )}
            </ModalBody>
          )}
          {selectedRequest?.status === 'PENDING' && (
            <ModalFooter>
              <SecondaryButton onClick={() => setSelectedRequest(null)}>Close</SecondaryButton>
              <DangerButton 
                onClick={() => setShowRejectModal(true)}
              >
                Reject
              </DangerButton>
              <PrimaryButton 
                onClick={() => handleApprove(selectedRequest)}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Approve'}
              </PrimaryButton>
            </ModalFooter>
          )}
        </ModalContent>
      </ModalOverlay>

      {/* Reject Modal */}
      <ModalOverlay $show={showRejectModal} onClick={() => setShowRejectModal(false)}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>❌ Reject Withdrawal Request</ModalTitle>
            <CloseButton onClick={() => setShowRejectModal(false)}>×</CloseButton>
          </ModalHeader>
          <ModalBody>
            <DetailSection>
              <SectionTitle>Rejection Reason *</SectionTitle>
              <Textarea
                value={rejectReason}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this withdrawal request..."
              />
            </DetailSection>
          </ModalBody>
          <ModalFooter>
            <SecondaryButton onClick={() => {
              setShowRejectModal(false);
              setRejectReason('');
            }}>
              Cancel
            </SecondaryButton>
            <DangerButton 
              onClick={handleReject}
              disabled={!rejectReason.trim() || processing}
            >
              {processing ? 'Rejecting...' : 'Reject Request'}
            </DangerButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </AdminShell>
  );
};

export default WithdrawalRequests;
